import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Invoice.css';

const INVOICE_VAT_RATE = 0.10;
const SMALL_NUMBER_WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS_WORDS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const convertBelowThousand = (value) => {
  const number = Math.floor(Number(value) || 0);

  if (number === 0) {
    return '';
  }

  if (number < 20) {
    return SMALL_NUMBER_WORDS[number];
  }

  if (number < 100) {
    const tens = Math.floor(number / 10);
    const remainder = number % 10;
    return `${TENS_WORDS[tens]}${remainder ? ` ${SMALL_NUMBER_WORDS[remainder]}` : ''}`;
  }

  const hundreds = Math.floor(number / 100);
  const remainder = number % 100;
  return `${SMALL_NUMBER_WORDS[hundreds]} Hundred${remainder ? ` ${convertBelowThousand(remainder)}` : ''}`;
};

const convertNumberToWords = (value) => {
  const number = Math.floor(Number(value) || 0);

  if (number === 0) {
    return 'Zero';
  }

  const segments = [
    { size: 10000000, label: 'Crore' },
    { size: 100000, label: 'Lakh' },
    { size: 1000, label: 'Thousand' },
  ];

  let remainder = number;
  const parts = [];

  segments.forEach(({ size, label }) => {
    if (remainder >= size) {
      const chunk = Math.floor(remainder / size);
      parts.push(`${convertBelowThousand(chunk)} ${label}`);
      remainder %= size;
    }
  });

  if (remainder > 0) {
    parts.push(convertBelowThousand(remainder));
  }

  return parts.join(' ').trim();
};

const formatAmountInWords = (amount) => {
  const total = Number(amount || 0);
  const taka = Math.floor(total);
  const paisa = Math.round((total - taka) * 100);
  const takaWords = convertNumberToWords(taka);

  if (paisa > 0) {
    return `${takaWords} Taka and ${convertNumberToWords(paisa)} Paisa Only`;
  }

  return `${takaWords} Taka Only`;
};

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobCard, setJobCard] = useState(null);
  const [challanDownloading, setChallanDownloading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState({
    companyName: 'VS Auto Service',
    companyAddress: '',
    companyBinNumber: '',
  });
  const [parts, setParts] = useState([]);
  const [labor, setLabor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchJobCardDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/jobcards/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobCard(response.data.jobCard);
      setCompanyProfile({
        companyName: response.data.companyProfile?.companyName || 'VS Auto Service',
        companyAddress: response.data.companyProfile?.companyAddress || '',
        companyBinNumber: response.data.companyProfile?.companyBinNumber || '',
      });
      setParts(response.data.parts || []);
      setLabor(response.data.labor || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load document details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchJobCardDetails();
    }
  }, [fetchJobCardDetails, id]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    return new Date(dateValue).toLocaleDateString('en-GB');
  };

  const formatMoney = (amount) => `Tk ${Number(amount || 0).toFixed(2)}`;

  const partsTotal = parts.reduce((sum, part) => sum + (Number(part.amount || part.Amount) || 0), 0);
  const laborTotal = labor.reduce((sum, item) => sum + (Number(item.amount || item.Amount) || 0), 0);

  const handlePrint = () => {
    window.print();
  };

  const handleVatChallan = async () => {
    try {
      setError('');
      setChallanDownloading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/jobcards/${id}/vat-challan`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const contentType = String(response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        const fallbackMessage = typeof response.data?.text === 'function'
          ? await response.data.text()
          : 'Failed to generate VAT challan';
        throw new Error(fallbackMessage || 'Failed to generate VAT challan');
      }

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `vat-challan-${jobCard?.JobNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
    } catch (err) {
      let message = err.message || 'Failed to generate VAT challan';

      if (err.response?.data && typeof err.response.data.text === 'function') {
        try {
          const rawText = await err.response.data.text();
          const parsed = rawText ? JSON.parse(rawText) : null;
          message = parsed?.message || parsed?.error || rawText || message;
        } catch (parseError) {
          message = message || 'Failed to generate VAT challan';
        }
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }

      setError(message);
    } finally {
      setChallanDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="invoice-page">
        <div className="invoice-loading">Loading document...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-page">
        <div className="invoice-error">{error}</div>
        <button className="btn-back" onClick={() => navigate('/invoices')}>Back to Inv & Est</button>
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="invoice-page">
        <div className="invoice-error">Document not found</div>
        <button className="btn-back" onClick={() => navigate('/invoices')}>Back to Inv & Est</button>
      </div>
    );
  }

  const documentType = String(jobCard.DocumentType || '').toLowerCase() === 'estimate' ? 'Estimate' : 'Invoice';
  const vehicle = [jobCard.CarNumber, jobCard.Year, jobCard.Make, jobCard.Model].filter(Boolean).join(' - ');
  const isPaid = String(jobCard.InvoiceStatus || '').trim().toLowerCase() === 'paid';
  const subTotal = partsTotal + laborTotal;
  const vatAmount = documentType === 'Invoice' ? subTotal * INVOICE_VAT_RATE : 0;
  const discountAmount = Number(jobCard.DiscountAmount || 0);
  const totalAmount = documentType === 'Invoice'
    ? Number(jobCard.TotalAmount || (subTotal + vatAmount))
    : subTotal;
  const netBillAmount = Math.max(0, totalAmount - discountAmount);
  const totalAmountInWords = formatAmountInWords(documentType === 'Invoice' ? netBillAmount : totalAmount);

  return (
    <div className="invoice-page">
      <div className="invoice-actions">
        <button className="btn-back" onClick={() => navigate('/invoices')}>Back</button>
        <div className="invoice-actions-right">
          {documentType === 'Invoice' && isPaid ? (
            <button
              className="btn-vat-challan"
              onClick={handleVatChallan}
              disabled={challanDownloading}
            >
              {challanDownloading ? 'Preparing VAT...' : 'VAT Challan'}
            </button>
          ) : null}
          <button className="btn-print" onClick={handlePrint}>
            {documentType === 'Estimate' ? 'Print Estimate' : 'Print Invoice'}
          </button>
        </div>
      </div>

      <div className="invoice-paper">
        {isPaid ? <div className="invoice-paid-seal">PAID</div> : null}
        <div className="invoice-header">
          <div className="invoice-company">
            <h1>{companyProfile.companyName || 'VS Auto Service'}</h1>
            {companyProfile.companyAddress ? <p><strong>{companyProfile.companyAddress}</strong></p> : null}
            {companyProfile.companyBinNumber ? <p><strong>BIN: {companyProfile.companyBinNumber}</strong></p> : null}
          </div>
          <div className="invoice-title">
            <h2>{documentType.toUpperCase()}</h2>
            <div className="invoice-number">#{jobCard.JobNumber || jobCard.JobCardId}</div>
          </div>
        </div>

        <div className="invoice-info-row">
          <div className="invoice-client-info">
            <h3>{documentType === 'Estimate' ? 'Estimate For:' : 'Bill To:'}</h3>
            <div className="info-block">
              <p><strong>{jobCard.ClientName || 'N/A'}</strong></p>
              <p>{jobCard.ClientPhone ? `Phone: ${jobCard.ClientPhone}` : ''}</p>
              <p>{jobCard.ClientEmail ? `Email: ${jobCard.ClientEmail}` : ''}</p>
              <p>{jobCard.ClientAddress ? `Address: ${jobCard.ClientAddress}` : ''}</p>
            </div>
          </div>
          <div className="invoice-vehicle-info">
            <h3>Vehicle Info:</h3>
            <div className="info-block">
              <p><strong>{vehicle || 'N/A'}</strong></p>
              <p>VIN: {jobCard.VIN || 'N/A'}</p>
              <p>Odometer: {jobCard.Odometer ?? jobCard.OdometerKm ?? 'N/A'} km</p>
              <p>CC: {jobCard.CC || 'N/A'}</p>
            </div>
          </div>
          <div className="invoice-meta">
            <p><strong>Date:</strong> {formatDate(jobCard.CreatedDate)}</p>
            <p><strong>Status:</strong> {jobCard.Status || 'Closed'}</p>
            <p><strong>Type:</strong> {documentType}</p>
            <p><strong>Inv / Est Status:</strong> {jobCard.InvoiceStatus || 'Ready'}</p>
            <p><strong>Adv Pay:</strong> {formatMoney(jobCard.AdvancePaidAmount)}</p>
            <p><strong>Discount:</strong> {formatMoney(jobCard.DiscountAmount)}</p>
            <p><strong>Received Amount:</strong> {formatMoney(jobCard.PaidAmount)}</p>
            <p><strong>Balance Due:</strong> {formatMoney(jobCard.BalanceDue)}</p>
            {jobCard.DiscountRefByEmployeeName ? (
              <p><strong>Discount Ref:</strong> {jobCard.DiscountRefByEmployeeName}</p>
            ) : null}
            {jobCard.DueRefByEmployeeName ? (
              <p><strong>Due Ref:</strong> {jobCard.DueRefByEmployeeName}</p>
            ) : null}
          </div>
        </div>

        {parts.length > 0 && (
          <div className="invoice-section">
            <h3>Parts</h3>
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {parts.map((part, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>{part.Description || part.description || part.PartNumber || part.partNumber || 'N/A'}</td>
                    <td className="text-center">{part.Quantity || part.quantity || 1}</td>
                    <td className="text-right">{formatMoney(part.Price || part.price)}</td>
                    <td className="text-right">{formatMoney(part.Amount || part.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {labor.length > 0 && (
          <div className="invoice-section">
            <h3>Labor</h3>
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Description</th>
                  <th>Hours</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {labor.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item.Name || item.name || item.Description || item.description || 'Labor Service'}</td>
                    <td className="text-center">{item.Hours || item.hours || 0}</td>
                    <td className="text-right">{formatMoney(item.Rate || item.rate)}</td>
                    <td className="text-right">{formatMoney(item.Amount || item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="invoice-totals">
          <div className="total-row">
            <span>Parts:</span>
            <span>{formatMoney(partsTotal)}</span>
          </div>
          <div className="total-row">
            <span>Labor:</span>
            <span>{formatMoney(laborTotal)}</span>
          </div>
          {documentType === 'Invoice' ? (
            <>
              <div className="total-row total-row-subtotal">
                <span>Subtotal:</span>
                <span>{formatMoney(subTotal)}</span>
              </div>
              <div className="total-row total-row-vat">
                <span>VAT (10%):</span>
                <span>{formatMoney(vatAmount)}</span>
              </div>
            </>
          ) : null}
          <div className="total-row grand-total">
            <span>Total:</span>
            <span>{formatMoney(totalAmount)}</span>
          </div>
          {discountAmount > 0 ? (
            <>
              <div className="total-row">
                <span>Discount:</span>
                <span>{formatMoney(discountAmount)}</span>
              </div>
              <div className="total-row grand-total">
                <span>Net Bill:</span>
                <span>{formatMoney(netBillAmount)}</span>
              </div>
            </>
          ) : null}
          <div className="invoice-total-words">
            <strong>In Words:</strong> {totalAmountInWords}
          </div>
        </div>

        <div className="invoice-footer">
          <div className="signature-section">
            <div className="signature-box">
              <p>Authorized Signature</p>
              <div className="signature-line"></div>
            </div>
            <div className="signature-box">
              <p>Customer Signature</p>
              <div className="signature-line"></div>
            </div>
          </div>
          <div className="invoice-notes">
            <p><strong>Notes:</strong> {documentType === 'Estimate' ? 'This estimate is provided for approval.' : 'Thank you for your business!'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
